(function () {
    "use strict";

    angular
        .module("elogbooksServices")
        .service("ahaWidgetService", AhaWidgetService);

    AhaWidgetService.$inject = [
        "$rootScope",
        "$state",
        "$window",
        "$timeout",
        "apiClient",
        "elbSettings",
        "userManager",
        "$resolves",
    ];

    function AhaWidgetService(
        $rootScope,
        $state,
        $window,
        $timeout,
        apiClient,
        elbSettings,
        userManager,
        $resolves
    ) {
        const self = this;

        const ACCOUNT = "axiomgrc";
        const APPLICATION_ID = "7527744917471197533";
        const EXCLUDED_ROUTES = [
            "sso",
            "login",
            "dashboard.terms-of-use",
            "dashboard.change-password",
            "emailverification"
        ];
        const AHA_SCRIPT_URL = "https://cdn.aha.io/assets/feedback/feedback.js";

        self.isInitialized = false;
        self.isInitializing = false;
        self.jwtToken = "";
        self.requestingToken = false;
        self.isBlocked = false; // Flag to prevent initialization when other modals are open

        let initWaitHandle = null;
        let routeHandlerAttached = false;

        // Helper function to check if JWT token is expired
        function isTokenExpired(token) {
            if (!token) return true;

            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const exp = payload.exp;
                if (!exp) return false; // No expiry set

                const now = Math.floor(Date.now() / 1000);
                return now >= exp;
            } catch (e) {
                return true; // Treat invalid tokens as expired
            }
        }

        // Public initialization kick-off (optional)
        initService();

        function initService() {
            // auto-init after a delay
            $timeout(initializeToken, 2000);
            // attach route change handler once
            if (!routeHandlerAttached) {
                $rootScope.$on("$stateChangeSuccess", onRouteChange);
                // Listen for user logout to reset token
                $rootScope.$on("userLogout", function() {
                    self.resetToken();
                    teardownWidget();
                });
                routeHandlerAttached = true;
            }
        }

        // Route change handling
        function onRouteChange() {
            teardownWidget();
            const shouldEnable =
                isEnabled() && isUserLoggedIn() && !isExcludedRoute() && !isLoggedInAsUser();

            if (shouldEnable) {
                // debounce initialization to let the new view settle
                $timeout(() => {
                    if (!self.isInitialized && !self.isInitializing) {
                        if (!self.jwtToken || isTokenExpired(self.jwtToken)) {
                            initializeToken(); // fetch token then init
                        } else {
                            waitForScriptAndInitialize(); // init with existing token
                        }
                    }
                }, 1000);
            }
        }

        // Fetch JWT and then init
        function initializeToken() {
            if (
                !isEnabled() ||
                !isUserLoggedIn() ||
                isExcludedRoute() ||
                self.requestingToken ||
                self.isBlocked
            )
                return;

            // Check if current token is expired
            if (self.jwtToken && !isTokenExpired(self.jwtToken)) {
                waitForScriptAndInitialize();
                return;
            }
            self.requestingToken = true;

            getJwtToken()
                .then((token) => {
                    self.jwtToken = token || "";
                    self.requestingToken = false;
                    waitForScriptAndInitialize();
                })
                .catch((err) => {
                    console.error(
                        "AhaWidgetService: Failed to get token:",
                        err
                    );
                });
        }

        // Load Aha script using their official pattern
        function ensureAhaScriptPresent() {
            return new Promise((resolve) => {
                // Check if already loaded (either queue function or full API)
                if ($window.aha && typeof $window.aha === "function") {
                    return resolve(true);
                }

                // Check if script already exists
                const existing = Array.from(
                    document.getElementsByTagName("script")
                ).some(
                    (s) =>
                        s.src &&
                        s.src.indexOf("/assets/feedback/feedback.js") !== -1
                );

                if (!existing) {
                    // Follow their official loading pattern
                    (function (w, d, s, o, f, js, fjs) {
                        w["aha-widget"] = o;
                        w[o] =
                            w[o] ||
                            function () {
                                (w[o].q = w[o].q || []).push(arguments);
                            };
                        js = d.createElement(s);
                        fjs = d.getElementsByTagName(s)[0];
                        js.id = o;
                        js.src = f;
                        js.async = 1;

                        // Add our own load handlers
                        js.onload = () => {
                            resolve(true);
                        };
                        js.onerror = () => {
                            resolve(false);
                        };

                        fjs.parentNode.insertBefore(js, fjs);
                    })($window, document, "script", "aha", AHA_SCRIPT_URL);
                } else {
                    resolve(true);
                }
            });
        }

        // Wait for window.aha and initialize
        function waitForScriptAndInitialize() {
            if (self.isInitialized || self.isInitializing || self.isBlocked)
                return;
            if (!self.jwtToken) return;

            self.isInitializing = true;

            // cancel any prior wait loop
            if (initWaitHandle) {
                $timeout.cancel(initWaitHandle);
                initWaitHandle = null;
            }

            let scriptEnsured = false;

            const attemptInit = () => {
                if ($window.aha) {
                    try {
                        $window.aha("initialize", {
                            account: ACCOUNT,
                            applicationId: APPLICATION_ID,
                            jwt: self.jwtToken,
                        });
                        self.isInitialized = true;
                        self.isInitializing = false;
                    } catch (e) {
                        initWaitHandle = $timeout(attemptInit, 1000);
                    }
                } else {
                    if (!scriptEnsured) {
                        scriptEnsured = true;
                        ensureAhaScriptPresent().then((success) => {
                            if (success) {
                                initWaitHandle = $timeout(attemptInit, 1000);
                            } else {
                                self.isInitializing = false;
                            }
                        });
                    } else {
                        initWaitHandle = $timeout(attemptInit, 1000);
                    }
                }
            };

            // Initial attempt
            attemptInit();
        }

        function teardownWidget() {
            // Cancel any pending initialization
            if (initWaitHandle) {
                $timeout.cancel(initWaitHandle);
                initWaitHandle = null;
            }

            // Destroy widget instances
            try {
                if ($window.aha) $window.aha("destroy");
            } catch (e) {
                console.warn("AhaWidgetService: destroy failed", e);
            }

            // Clean up globals
            try {
                delete $window.aha;
                delete $window["aha-widget"];
            } catch (e) {
                $window.aha = undefined;
                $window["aha-widget"] = undefined;
            }

            // Remove script tag for fresh reload
            try {
                const ahaScript = document.querySelector(
                    'script[src*="/assets/feedback/feedback.js"]'
                );
                if (ahaScript) ahaScript.remove();
            } catch (e) {
                console.warn("AhaWidgetService: Failed to remove script", e);
            }

            // Reset state
            self.isInitialized = false;
            self.isInitializing = false;
        }

        function isEnabled() {
            const setting = elbSettings.getSetting("aha_polls_enabled", false);
            return setting ? setting.value : false;
        }

        function isUserLoggedIn() {
            return !!userManager.user;
        }

        function isExcludedRoute() {
            const currentRoute = $state.current.name || "";
            return EXCLUDED_ROUTES.includes(currentRoute);
        }

        function isLoggedInAsUser() {
            return !! userManager.originalUser;
        }

        async function getJwtToken() {
            try {
                const resolves = $resolves();
                const rootResource = resolves && resolves.rootResourceResponse;
                if (rootResource && rootResource.getLink) {
                    const url = rootResource.getLink("aha-polls-auth");
                    if (url) {
                        const response = await apiClient.get(url);
                        return response?.token || "";
                    }
                }
                return "";
            } catch {
                return "";
            }
        }

        // Public API for external services
        self.destroy = function () {
            teardownWidget();
        };

        self.block = function () {
            self.isBlocked = true;
            teardownWidget();
        };

        self.unblock = function () {
            self.isBlocked = false;
        };

        self.reinitialize = function () {
            if (
                isEnabled() &&
                isUserLoggedIn() &&
                !isExcludedRoute() &&
                !self.isBlocked
            ) {
                if (!self.jwtToken || isTokenExpired(self.jwtToken)) {
                    initializeToken();
                } else {
                    waitForScriptAndInitialize();
                }
            }
        };

        self.resetToken = function () {
            self.jwtToken = "";
        };
    }
})();
